import { useState, useEffect, useCallback, useMemo } from 'react';
import { projectsApi, Project } from '../services/api';

interface ProjectsCache {
  owned: Project[];
  shared: Project[];
}

// Module-level cache — project lists are shared across home, chat, history, documents, etc.
let _cache: ProjectsCache | null = null;
let _pending: Promise<ProjectsCache> | null = null;
let _revision = 0;
const _revisionListeners = new Set<(rev: number) => void>();

function bumpRevision() {
  _revision += 1;
  _revisionListeners.forEach((listener) => listener(_revision));
}

export function invalidateProjectsCache() {
  _cache = null;
  _pending = null;
}

async function fetchProjects(): Promise<ProjectsCache> {
  if (_cache) return _cache;
  if (!_pending) {
    _pending = Promise.all([projectsApi.list(), projectsApi.listShared()]).then(
      ([owned, shared]) => ({ owned, shared }),
    );
  }
  try {
    const data = await _pending;
    _cache = data;
    return data;
  } finally {
    _pending = null;
  }
}

export async function refreshProjectsCache(): Promise<ProjectsCache> {
  invalidateProjectsCache();
  const data = await fetchProjects();
  bumpRevision();
  return data;
}

export function useProjects() {
  const [revision, setRevision] = useState(_revision);
  const [owned, setOwned] = useState<Project[]>(_cache?.owned ?? []);
  const [shared, setShared] = useState<Project[]>(_cache?.shared ?? []);
  const [loading, setLoading] = useState(_cache === null);

  useEffect(() => {
    const listener = (rev: number) => setRevision(rev);
    _revisionListeners.add(listener);
    return () => {
      _revisionListeners.delete(listener);
    };
  }, []);

  const refresh = useCallback(async () => {
    invalidateProjectsCache();
    setLoading(true);
    try {
      const data = await refreshProjectsCache();
      setOwned(data.owned);
      setShared(data.shared);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const invalidate = useCallback(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;

    if (_cache) {
      setOwned(_cache.owned);
      setShared(_cache.shared);
      setLoading(false);
      return;
    }

    setLoading(true);
    void fetchProjects()
      .then((data) => {
        if (!cancelled) {
          setOwned(data.owned);
          setShared(data.shared);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [revision]);

  const allProjects = useMemo(() => [...owned, ...shared], [owned, shared]);

  return {
    projects: owned,
    sharedProjects: shared,
    allProjects,
    loading,
    refresh,
    invalidate,
  };
}
